import React, { useState, useEffect, useRef, useCallback } from 'react';
import {
  Box,
  Paper,
  Typography,
  TextField,
  Button,
  List,
  ListItem,
  Avatar,
  Chip,
  CircularProgress,
  Alert,
  Divider
} from '@mui/material';
import { Send as SendIcon, Person as PersonIcon } from '@mui/icons-material';
import { apiClient } from '@/utils/api-client';
import { useAuth } from '@/contexts/AuthContext';

interface BookingMessage {
  bookingId: string;
  createdAt: string;
  messageId: string;
  userEmail: string;
  message: string;
  messageType: 'text' | 'image' | 'file';
  userType: 'host' | 'guest';
  userName?: string;
  userProfilePicture?: string;
  readBy?: string[];
  updatedAt: string;
}

interface BookingMessagesProps {
  bookingId: string;
  otherParticipant: {
    email: string;
    name: string;
    type: 'host' | 'guest';
  };
  listingTitle?: string;
}

const BookingMessages: React.FC<BookingMessagesProps> = ({
  bookingId,
  otherParticipant,
  listingTitle
}) => {
  const { userProfile: user } = useAuth();
  const [messages, setMessages] = useState<BookingMessage[]>([]);
  const [newMessage, setNewMessage] = useState('');
  const [loading, setLoading] = useState(true);
  const [sending, setSending] = useState(false);
  const [error, setError] = useState<string | null>(null);
  const messagesEndRef = useRef<HTMLDivElement>(null);

  const loadMessages = useCallback(async () => {
    try {
      setLoading(true);
      setError(null);

      const response = await apiClient.get(`/chat/messages/${bookingId}`);
      setMessages((response.data as any).messages || []);
    } catch (err) {
      console.error('Error loading messages:', err);
      setError('Failed to load messages');
    } finally {
      setLoading(false);
    }
  }, [bookingId]);

  useEffect(() => {
    scrollToBottom();
  }, [messages]);

  const scrollToBottom = () => {
    messagesEndRef.current?.scrollIntoView({ behavior: 'smooth' });
  };

  useEffect(() => {
    loadMessages();
  }, [loadMessages]);

  const sendMessage = async () => {
    if (!newMessage.trim() || sending) return;

    try {
      setSending(true);
      setError(null);

      const response = await apiClient.post(`/chat/messages/${bookingId}`, {
        message: newMessage.trim(),
        messageType: 'text',
        userName: user?.name || user?.email,
        userProfilePicture: user?.photoUrl
      });

      // Add the new message to the list
      setMessages(prev => [...prev, (response.data as any).messageData]);
      setNewMessage('');
    } catch (err) {
      console.error('Error sending message:', err);
      setError('Failed to send message');
    } finally {
      setSending(false);
    }
  };

  const handleKeyPress = (event: React.KeyboardEvent) => {
    if (event.key === 'Enter' && !event.shiftKey) {
      event.preventDefault();
      sendMessage();
    }
  };

  const formatTime = (timestamp: string) => {
    return new Date(timestamp).toLocaleString();
  };

  const isMyMessage = (message: BookingMessage) => {
    return message.userEmail === user?.email;
  };

  if (loading) {
    return (
      <Box display="flex" justifyContent="center" alignItems="center" height="400px">
        <CircularProgress />
      </Box>
    );
  }

  return (
    <Paper elevation={1} sx={{ height: '600px', display: 'flex', flexDirection: 'column' }}>
      {/* Header */}
      <Box sx={{ p: 2, borderBottom: 1, borderColor: 'divider' }}>
        <Typography variant="h6" gutterBottom>
          {listingTitle || 'Booking Messages'}
        </Typography>
        <Box display="flex" alignItems="center" gap={1}>
          <Avatar sx={{ width: 32, height: 32 }}>
            {otherParticipant.name.charAt(0).toUpperCase()}
          </Avatar>
          <Typography variant="body2" color="text.secondary">
            {otherParticipant.name}
          </Typography>
          <Chip 
            label={otherParticipant.type} 
            size="small" 
            color={otherParticipant.type === 'host' ? 'primary' : 'secondary'}
          />
        </Box>
      </Box>

      {/* Messages */}
      <Box sx={{ flex: 1, overflow: 'auto', p: 1 }}>
        {error && (
          <Alert severity="error" sx={{ mb: 2 }}>
            {error}
          </Alert>
        )}

        {messages.length === 0 ? (
          <Box 
            display="flex" 
            flexDirection="column" 
            alignItems="center" 
            justifyContent="center" 
            height="100%"
            color="text.secondary"
          >
            <PersonIcon sx={{ fontSize: 48, mb: 2 }} />
            <Typography variant="body1">
              No messages yet. Start the conversation!
            </Typography>
          </Box>
        ) : (
          <List sx={{ py: 0 }}>
            {messages.map((message, index) => {
              const isMe = isMyMessage(message);
              const showDate = index === 0 || 
                new Date(messages[index - 1].createdAt).toDateString() !== 
                new Date(message.createdAt).toDateString();

              return (
                <React.Fragment key={message.messageId}>
                  {showDate && (
                    <Box sx={{ textAlign: 'center', my: 2 }}>
                      <Divider>
                        <Chip 
                          label={new Date(message.createdAt).toLocaleDateString()} 
                          size="small" 
                          variant="outlined"
                        />
                      </Divider>
                    </Box>
                  )}
                  
                  <ListItem
                    sx={{
                      display: 'flex',
                      flexDirection: 'column',
                      alignItems: isMe ? 'flex-end' : 'flex-start',
                      py: 0.5
                    }}
                  >
                    <Box
                      sx={{
                        maxWidth: '70%',
                        bgcolor: isMe ? 'primary.main' : 'grey.100',
                        color: isMe ? 'primary.contrastText' : 'text.primary',
                        borderRadius: 2,
                        px: 2,
                        py: 1,
                        mb: 0.5
                      }}
                    >
                      <Typography variant="body1">
                        {message.message}
                      </Typography>
                    </Box>
                    
                    <Typography 
                      variant="caption" 
                      color="text.secondary"
                      sx={{ px: 1 }}
                    >
                      {message.userName || message.userEmail} • {formatTime(message.createdAt)}
                    </Typography>
                  </ListItem>
                </React.Fragment>
              );
            })}
          </List>
        )}
        <div ref={messagesEndRef} />
      </Box>

      {/* Message Input */}
      <Box sx={{ p: 2, borderTop: 1, borderColor: 'divider' }}>
        <Box display="flex" gap={1}>
          <TextField
            fullWidth
            multiline
            maxRows={3}
            placeholder="Type your message..."
            value={newMessage}
            onChange={(e) => setNewMessage(e.target.value)}
            onKeyPress={handleKeyPress}
            disabled={sending}
            size="small"
          />
          <Button
            variant="contained"
            onClick={sendMessage}
            disabled={!newMessage.trim() || sending}
            sx={{ minWidth: 'auto', px: 2 }}
          >
            {sending ? <CircularProgress size={20} /> : <SendIcon />}
          </Button>
        </Box>
      </Box>
    </Paper>
  );
};

export default BookingMessages;